import { Injectable } from '@angular/core';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { Subject, Observable } from 'rxjs';
import { Context } from '../shared/services/context.service';
import { ConversationMessageResponse } from './conversation.service';
import { TreatmentPlanNotification } from '../model/treatment-plan-notification.model';

@Injectable({
  providedIn: 'root'
})
export class MessagingWebsocketService {

  private client: Client;
  private connected = false;
  private treatmentPlanSubject = new Subject<TreatmentPlanNotification>();
  private treatmentPlanSubscription: StompSubscription = null;

  constructor() {}

  private buildWebsocketUrl(): string {
    // environment.apiUrl already includes protocol and host, may end with a slash
    var apiUrl = Context.apiUrl;
    if (apiUrl.charAt(apiUrl.length - 1) === '/') {
      apiUrl = apiUrl.substring(0, apiUrl.length - 1);
    }
    return apiUrl + '/commentChat';
  }

  connect(): Promise<void> {
    var self = this;

    if (this.connected && this.client && this.client.connected) {
      return Promise.resolve();
    }

    return new Promise(function (resolve, reject) {
      var wsUrl = self.buildWebsocketUrl();
      var token = localStorage.getItem('ACCESS_TOKEN');

      self.client = new Client({
        webSocketFactory: function () {
          return new SockJS(wsUrl);
        },
        reconnectDelay: 5000,
        connectHeaders: token ? { Authorization: token } : {},
        debug: function () {
          // silence logs in production; hook here if needed
        }
      });

      self.client.onConnect = function () {
        self.connected = true;
        resolve();
      };

      self.client.onStompError = function (frame) {
        // keep simple for now
        reject(frame);
      };

      self.client.activate();
    });
  }

  disconnect(): void {
    if (this.client && this.client.active) {
      this.client.deactivate();
    }
    this.connected = false;
  }

  subscribeToConversation(conversationId: number): { subscription: StompSubscription; messages$: Observable<ConversationMessageResponse> } {
    var subject = new Subject<ConversationMessageResponse>();
    var self = this;
    var subscription: StompSubscription = null;

    var doSubscribe = function () {
      if (!self.client || !self.client.connected) {
        return;
      }
      var destination = '/comments/conversations/' + conversationId;

      subscription = self.client.subscribe(destination, function (message: IMessage) {
        if (message && message.body) {
          try {
            var payload = JSON.parse(message.body) as ConversationMessageResponse;
            subject.next(payload);
          } catch (e) {
            // ignore malformed payloads
          }
        }
      });
    };

    if (!this.client || !this.client.connected) {
      this.connect().then(function () {
        doSubscribe();
      }).catch(function () {
        // ignore connect errors here; caller will just not get realtime updates
      });
    } else {
      doSubscribe();
    }

    return {
      subscription: subscription,
      messages$: subject.asObservable()
    };
  }

  /**
   * Subscribe to per-user treatment plan notifications queue.
   * Emits raw payloads so consumers can decide how to react (badge, refresh, etc).
   */
  subscribeToTreatmentPlanQueue(): Observable<TreatmentPlanNotification> {
    var self = this;

    var doSubscribe = function () {
      if (!self.client || !self.client.connected || self.treatmentPlanSubscription) {
        return;
      }

      self.treatmentPlanSubscription = self.client.subscribe('/user/queue/treatment-plan', function (message: IMessage) {
        if (message && message.body) {
          try {
            var payload = JSON.parse(message.body) as TreatmentPlanNotification;
            self.treatmentPlanSubject.next(payload);
          } catch (e) {
            // ignore malformed payloads
          }
        }
      });
    };

    if (!this.client || !this.client.connected) {
      this.connect().then(function () {
        doSubscribe();
      }).catch(function () {
        // silent failure: consumers will simply not receive notifications
      });
    } else {
      doSubscribe();
    }

    return this.treatmentPlanSubject.asObservable();
  }
}


